import React, { useState } from 'react';
import { Project, Equipment, AMIDataPoint } from '../types/project';
import { CalculationResults } from '../lib/calculations';
import { TraditionalCalcResults } from '../lib/calc/types';
import { formatReportData } from '../lib/reportGenerator';
import { generateHTMLReport } from '../utils/reportGenerator';
import { Printer, CheckSquare, CheckCircle } from 'lucide-react';
import { Tooltip } from './Tooltip';

interface ReportViewProps {
  project: Partial<Project>;
  equipment: Equipment[];
  amiData: AMIDataPoint[];
  results: CalculationResults;
  onMarkComplete?: () => void;
  /** Stamps the report DRAFT — INCOMPLETE. See ReportData.isDraft. */
  isDraft?: boolean;
  /** Report basis; absent means measured — every pre-existing caller. */
  method?: 'measured' | 'traditional';
  traditionalResults?: TraditionalCalcResults;
  /** True when the measured method genuinely ran; gates the Methods Computed
   * block together with traditionalResults. */
  measuredRan?: boolean;
  /** Firm logo for the letterhead — see ReportData.firmLogoSrc. */
  firmLogoSrc?: string | null;
}

/**
 * The print path renders the same template as the preview and PDF paths — one
 * copy of the report markup, in utils/reportGenerator.ts. Kept exported under
 * this name because callers and tests import it from here.
 */
export { generateHTMLReport as generatePrintHTML } from '../utils/reportGenerator';

export function ReportView({
  project,
  equipment,
  amiData,
  results,
  onMarkComplete,
  isDraft = false,
  method,
  traditionalResults,
  measuredRan,
  firmLogoSrc = null,
}: ReportViewProps) {
  const [printing, setPrinting] = useState(false);

  const isTraditional = method === 'traditional' && !!traditionalResults;

  const reportData = {
    project: project as Project,
    equipment,
    amiData,
    results,
    reportDate: new Date().toISOString(),
    isDraft,
    method: isTraditional ? ('traditional' as const) : undefined,
    traditionalResults,
    measuredRan,
    firmLogoSrc,
  };

  const data = formatReportData(reportData);
  // The measured method's section number comes from the project's code
  // edition — 220.87 under 2020/2023, 120.87 under the 2026 Article 120
  // reorg — so this screen, like the printed report, holds no literal.
  const cite = data.measured_citation;
  const isPassing = data.adequacy_result === 'PASS';

  const propertyLine = [data.customer_address, data.customer_city_state_zip]
    .filter(Boolean)
    .join(', ');

  const identityMeta = [
    data.customer_name,
    data.permit_number ? `Permit ${data.permit_number}` : '',
    data.ahj_jurisdiction,
    data.report_date,
  ]
    .filter(Boolean)
    .join(' \u00b7 ');

  const handlePrint = () => {
    setPrinting(true);

    try {
      const htmlContent = generateHTMLReport(data);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        URL.revokeObjectURL(url);
        setTimeout(() => {
          document.body.removeChild(iframe);
          setPrinting(false);
        }, 1000);
      };

      iframe.src = url;
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to open print dialog');
      setPrinting(false);
    }
  };

  return (
    <div data-testid="report-container" className="bg-white">
      <div className="no-print mb-6 flex justify-between items-center">
        <div>
          {onMarkComplete && project.status !== 'completed' && (
            <button
              onClick={onMarkComplete}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <CheckSquare className="w-5 h-5" />
              <span>Mark Complete</span>
            </button>
          )}
          {project.status === 'completed' && (
            <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-800 rounded-md border border-green-300">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Project Completed</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            disabled={printing}
            data-testid="btn-download-pdf"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {printing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Opening...
              </>
            ) : (
              <>
                <Printer size={20} />
                Print Report
              </>
            )}
          </button>
          <Tooltip text="Opens the browser print dialog. Select your printer or choose 'Save as PDF' to produce a PDF for emailing or permit submission." />
        </div>
      </div>

      {isTraditional ? (
        // The traditional layout has exactly one source of truth — the HTML
        // template — previewed here rather than duplicated as JSX the way the
        // measured layout historically was. Print uses the same HTML.
        <iframe
          title="Report preview"
          data-testid="traditional-report-preview"
          className="w-full bg-white border border-gray-200 rounded"
          style={{ minHeight: '1400px' }}
          srcDoc={generateHTMLReport(data)}
        />
      ) : (
      <div className="report-content bg-white p-8 max-w-[8.5in] mx-auto">
        <style>{`
          @media print {
            @page {
              margin: 0.75in;
              size: letter;
            }
            body { margin: 0; }
            .no-print { display: none !important; }
            .report-content {
              padding: 0 !important;
              max-width: 100% !important;
            }
            .page-break { page-break-before: always; }
          }
        `}</style>

        {data.firm_logo_src && (
          <div className="text-center mb-4" data-testid="report-firm-logo">
            <img
              src={data.firm_logo_src}
              alt={data.company_name || 'Firm logo'}
              className="inline-block max-h-14 max-w-[2.6in] w-auto h-auto object-contain"
            />
          </div>
        )}

        {data.is_draft && (
          <div
            data-testid="report-draft-banner"
            className="border-[3px] border-red-700 bg-red-50 text-red-900 px-4 py-3 mb-5 text-center font-bold tracking-wide"
          >
            DRAFT — INCOMPLETE
            <span className="block font-normal text-xs tracking-normal mt-1">
              Required information is missing from this evaluation. This document is not
              suitable for submission to an authority having jurisdiction.
            </span>
          </div>
        )}

        <h1 className="text-2xl font-bold text-center mb-2 uppercase">
          NEC {cite} Service Adequacy Evaluation Report
        </h1>

        {/* Property, customer, permit, jurisdiction and date on one line, so an
            AHJ can confirm what they are looking at before reading the
            determination. Every field here repeats in Project and Preparer. */}
        <div className="text-center border-b border-black pb-3 mb-5">
          {propertyLine && (
            <span className="block font-bold text-lg">{propertyLine}</span>
          )}
          <span className="text-gray-700">{identityMeta}</span>
        </div>

        <div
          className={`border-4 p-6 text-center mb-8 ${
            isPassing
              ? 'border-green-600 bg-green-50'
              : 'border-red-600 bg-red-50'
          }`}
        >
          <h2
            className={`text-2xl font-bold mb-3 ${
              isPassing ? 'text-green-800' : 'text-red-800'
            }`}
          >
            SERVICE ADEQUACY DETERMINATION: {data.adequacy_result}
          </h2>
          <p className="text-lg">{data.adequacy_status_text}</p>
        </div>

        <section className="mb-8">
          <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
            NEC {cite} Load Calculation Summary
          </h2>
          <table className="w-full border-collapse border border-black">
            <tbody>
              <tr>
                <td className="border border-black p-2 font-bold w-3/5">
                  Existing Maximum Demand (from AMI data)
                </td>
                <td className="border border-black p-2 text-right">
                  {data.existing_demand_kva} kVA
                </td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-bold">
                  125% of Existing Demand (per NEC {cite})
                </td>
                <td className="border border-black p-2 text-right">
                  {data.existing_demand_125_kva} kVA
                </td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-bold">
                  New Concurrent Load (Added Equipment
                  {data.new_load_factors_applied
                    ? `, Table 120.83 demand factors applied to ${data.total_concurrent_connected_kva} kVA connected`
                    : ''}
                  )
                </td>
                <td className="border border-black p-2 text-right">
                  {data.total_concurrent_new_load_kva} kVA
                </td>
              </tr>
              {data.removed_credit_applied && (
                <tr>
                  <td className="border border-black p-2 font-bold">
                    Removed Load Credit (40% of removed equipment running in the peak season)
                  </td>
                  <td className="border border-black p-2 text-right">
                    &minus;{data.removed_credit_kva} kVA
                  </td>
                </tr>
              )}
              <tr className="bg-blue-50">
                <td className="border border-black p-2 font-bold">
                  Adjusted Demand (125% Existing + New Concurrent
                  {data.removed_credit_applied ? ' − Removed' : ''})
                </td>
                <td className="border border-black p-2 text-right font-bold">
                  {data.adjusted_demand_kva} kVA
                </td>
              </tr>
              <tr className="bg-gray-100">
                <td className="border border-black p-2 font-bold">
                  Service Capacity ({data.service_rating_amps}A @{' '}
                  {data.service_voltage_text})
                </td>
                <td className="border border-black p-2 text-right font-bold">
                  {data.service_capacity_kva} kVA
                </td>
              </tr>
              <tr
                className={
                  isPassing ? 'bg-green-100' : 'bg-red-100'
                }
              >
                <td className="border border-black p-2 font-bold">
                  Available Margin
                </td>
                <td className="border border-black p-2 text-right font-bold">
                  {data.available_margin_kva} kVA
                </td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-bold">
                  Service Utilization
                </td>
                <td className="border border-black p-2 text-right">
                  {data.utilization_percent}%
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <p className="text-sm text-gray-700 -mt-6 mb-8">{data.calculation_basis}</p>

        <div className="page-break"></div>

        <section className="mb-8">
          <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
            Project and Preparer
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <h3 className="text-lg font-bold mb-2">Project</h3>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
                <dt className="font-bold">Customer:</dt>
                <dd>{data.customer_name}</dd>

                <dt className="font-bold">Property:</dt>
                <dd>
                  {data.customer_address}
                  {data.customer_city_state_zip && (
                    <>
                      <br />
                      {data.customer_city_state_zip}
                    </>
                  )}
                </dd>

                <dt className="font-bold">Permit No.:</dt>
                <dd>{data.permit_number || 'N/A'}</dd>

                <dt className="font-bold">Jurisdiction:</dt>
                <dd>{data.ahj_jurisdiction || 'N/A'}</dd>

                <dt className="font-bold">Report Date:</dt>
                <dd>{data.report_date}</dd>
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Prepared By</h3>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
                <dt className="font-bold">Preparer:</dt>
                <dd>
                  {data.prepared_by}
                  {data.preparer_title ? `, ${data.preparer_title}` : ''}
                </dd>

                <dt className="font-bold">License No.:</dt>
                <dd>{data.preparer_license || 'N/A'}</dd>

                <dt className="font-bold">Company:</dt>
                <dd>
                  {data.company_name || 'N/A'}
                  {data.company_address && (
                    <>
                      <br />
                      {data.company_address}
                    </>
                  )}
                </dd>
              </dl>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
            Existing Service and Measured Demand
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <h3 className="text-lg font-bold mb-2">Existing Service</h3>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
                <dt className="font-bold">Voltage:</dt>
                <dd>{data.service_voltage_text}</dd>

                <dt className="font-bold">Rating:</dt>
                <dd>{data.service_rating_amps} Amperes</dd>

                <dt className="font-bold">Capacity:</dt>
                <dd>{data.service_capacity_kva} kVA</dd>
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">AMI Measurement</h3>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
                <dt className="font-bold">Provider:</dt>
                <dd>{data.ami_data_provider || 'N/A'}</dd>

                <dt className="font-bold">Period:</dt>
                <dd>{data.ami_measurement_period || 'N/A'}</dd>

                <dt className="font-bold">Interval:</dt>
                <dd>
                  {data.ami_interval_minutes
                    ? `${data.ami_interval_minutes} minutes`
                    : 'N/A'}
                </dd>

                <dt className="font-bold">Occupancy:</dt>
                <dd>{data.ami_occupancy_status || 'N/A'}</dd>

                <dt className="font-bold">Peak Demand:</dt>
                <dd>
                  {data.max_recorded_demand_kw} kW ({data.max_recorded_demand_kva} kVA)
                </dd>

                <dt className="font-bold">Peak Time:</dt>
                <dd>
                  {data.peak_timestamp}
                  {data.peak_season ? ` (${data.peak_season})` : ''}
                </dd>
              </dl>
            </div>
          </div>

          {data.ami_site_notes && (
            <>
              <h3 className="text-lg font-bold mt-6 mb-2">
                Notes on the Building
              </h3>
              <p className="text-gray-700 whitespace-pre-wrap">{data.ami_site_notes}</p>
            </>
          )}

          {data.ami_data_quality_notes && (
            <>
              <h3 className="text-lg font-bold mt-6 mb-2">
                Data Quality Notes
              </h3>
              <p className="text-gray-700">{data.ami_data_quality_notes}</p>
            </>
          )}

          {data.ami_merge_disclosure && (
            <>
              <h3 className="text-lg font-bold mt-6 mb-2">
                Interval Data Conflict Resolution
              </h3>
              <p className="text-gray-700">{data.ami_merge_disclosure}</p>
            </>
          )}

          {data.data_quality_exclusions && (
            <div data-testid="report-data-quality-exclusions">
              <h3 className="text-lg font-bold mt-6 mb-2">
                Data Quality Exclusions
              </h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-gray-700">
                <dt className="font-semibold">Raw peak (all readings):</dt>
                <dd data-testid="report-raw-peak">{data.data_quality_exclusions.raw_peak}</dd>
                <dt className="font-semibold">Peak used in this calculation:</dt>
                <dd data-testid="report-used-peak">{data.data_quality_exclusions.used_peak}</dd>
                <dt className="font-semibold">Total time excluded:</dt>
                <dd>{data.data_quality_exclusions.total_hours}</dd>
              </dl>
              <table className="w-full border-collapse border border-black mt-3 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black p-2 text-left">Excluded window</th>
                    <th className="border border-black p-2 text-left">Flagged by</th>
                    <th className="border border-black p-2 text-left">Reason</th>
                    <th className="border border-black p-2 text-left">Note</th>
                    <th className="border border-black p-2 text-left">Excluded by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data_quality_exclusions.windows.map((w, i) => (
                    <tr key={i}>
                      <td className="border border-black p-2">{w.window}</td>
                      <td className="border border-black p-2">{w.check}</td>
                      <td className="border border-black p-2">{w.reason}</td>
                      <td className="border border-black p-2">{w.note}</td>
                      <td className="border border-black p-2">{w.by}, {w.at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-gray-700 mt-2">{data.data_quality_exclusions.statement}</p>
            </div>
          )}
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
            Equipment Schedule - Proposed New Loads
          </h2>
          <table className="w-full border-collapse border border-black">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black p-2 text-left">
                  Equipment Name
                </th>
                <th className="border border-black p-2 text-left">Type</th>
                <th className="border border-black p-2 text-left">Source</th>
                <th className="border border-black p-2 text-left">Qty</th>
                <th className="border border-black p-2 text-left">Voltage</th>
                <th className="border border-black p-2 text-left">Amps</th>
                <th className="border border-black p-2 text-left">kW</th>
                <th className="border border-black p-2 text-left">kVA</th>
                <th className="border border-black p-2 text-left">
                  Concurrent
                </th>
                {data.new_load_factors_applied && (
                  <>
                    <th className="border border-black p-2 text-left">Factor</th>
                    <th className="border border-black p-2 text-left">Counted kVA</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.equipment_list.map((eq, idx) => (
                <React.Fragment key={idx}>
                  <tr>
                    <td className="border border-black p-2">
                      {eq.equipment_name || 'N/A'}
                    </td>
                    <td className="border border-black p-2">
                      {eq.equipment_type || 'N/A'}
                    </td>
                    <td className="border border-black p-2">
                      {eq.source || 'Nameplate'}
                    </td>
                    <td className="border border-black p-2">
                      {eq.quantity || '1'}
                    </td>
                    <td className="border border-black p-2">
                      {eq.voltage || 'N/A'}V
                    </td>
                    <td className="border border-black p-2">
                      {eq.amps || 'N/A'}
                    </td>
                    <td className="border border-black p-2">{eq.kw || 'N/A'}</td>
                    <td className="border border-black p-2">{eq.kva}</td>
                    <td className="border border-black p-2">
                      {eq.is_concurrent}
                    </td>
                    {data.new_load_factors_applied && (
                      <>
                        <td className="border border-black p-2">{eq.demand_factor}</td>
                        <td className="border border-black p-2">{eq.counted_kva}</td>
                      </>
                    )}
                  </tr>
                  {eq.concurrency_reason && (
                    <tr>
                      <td
                        colSpan={data.new_load_factors_applied ? 11 : 9}
                        className="border border-black px-2 py-1 text-xs text-gray-500 italic bg-gray-50"
                        style={{ borderTop: 'none' }}
                      >
                        Concurrency basis: {eq.concurrency_reason}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="bg-gray-100 font-bold">
                <td colSpan={7} className="border border-black p-2">
                  Total All New Equipment
                </td>
                <td className="border border-black p-2">
                  {data.total_all_new_load_kva}
                </td>
                <td className="border border-black p-2"></td>
                {data.new_load_factors_applied && (
                  <>
                    <td className="border border-black p-2"></td>
                    <td className="border border-black p-2"></td>
                  </>
                )}
              </tr>
              <tr className="bg-blue-50 font-bold">
                <td colSpan={7} className="border border-black p-2">
                  Total Concurrent New Equipment
                </td>
                <td className="border border-black p-2">
                  {data.new_load_factors_applied
                    ? data.total_concurrent_connected_kva
                    : data.total_concurrent_new_load_kva}
                </td>
                <td className="border border-black p-2"></td>
                {data.new_load_factors_applied && (
                  <>
                    <td className="border border-black p-2"></td>
                    <td className="border border-black p-2">
                      {data.total_concurrent_new_load_kva}
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </section>

        {/* Equipment taken out with this work — same content as the HTML
            report (lib/removedLoadDisclosure.ts). */}
        {(data.removed_list ?? []).length > 0 && (
          <section className="mb-8" data-testid="report-removed-equipment">
            <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
              Equipment Removed With This Work
            </h2>
            <table className="w-full border-collapse border border-black">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2 text-left">Equipment Name</th>
                  <th className="border border-black p-2 text-left">Type</th>
                  <th className="border border-black p-2 text-left">kVA</th>
                  <th className="border border-black p-2 text-left">Ran at Peak Season</th>
                  {data.removed_credit_applied && (
                    <th className="border border-black p-2 text-left">Credit kVA</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(data.removed_list ?? []).map((eq, idx) => (
                  <tr key={idx}>
                    <td className="border border-black p-2">{eq.equipment_name || 'N/A'}</td>
                    <td className="border border-black p-2">{eq.equipment_type || 'N/A'}</td>
                    <td className="border border-black p-2">{eq.kva}</td>
                    <td className="border border-black p-2">{eq.is_concurrent}</td>
                    {data.removed_credit_applied && (
                      <td className="border border-black p-2">{eq.credit_kva}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.removed_disclosure && (
              <p className="mt-3 text-gray-700">{data.removed_disclosure}</p>
            )}
          </section>
        )}

        {data.assumptions_notes && (
          <section className="mb-8">
            <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
              Assumptions and Notes
            </h2>
            <p className="whitespace-pre-wrap text-gray-700">
              {data.assumptions_notes}
            </p>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-xl font-bold border-b-2 border-black pb-2 mb-4">
            Methodology
          </h2>
          {/* The measured method applies only to existing installations: a new service
              has no measured demand history to draw on. That scope statement is
              fixed prose here rather than a labelled field, because there is
              nothing for the user to choose. */}
          <p className="mb-4 text-gray-700">
            The service evaluated here is an existing installation; NEC {cite}
            establishes existing load by actual measurement and applies only
            where such a demand history exists. This evaluation was performed in
            accordance with the National Electrical Code (NEC) Section {cite}{' '}
            ({data.measured_article}, {data.code_edition} edition).
            The existing demand was measured using Advanced Metering
            Infrastructure (AMI) data over the specified measurement period. The
            adjusted demand calculation applies a 125% factor to the maximum
            recorded existing demand and adds{' '}
            {data.new_load_factors_applied
              ? 'concurrent new loads at the Table 120.83 demand factors described below.'
              : 'the full nameplate rating of concurrent new loads.'}
          </p>
          <p className="mb-4 text-gray-700">
            NEC {cite} is a self-contained alternative method. The 125% factor applied to measured existing demand is the method's built-in conservatism, accounting for load growth and measurement variability.{' '}
            {data.new_load_factors_applied
              ? 'Proposed new loads are entered at full nameplate (or MCA) rating and then take the Table 120.83 demand factors described below; no other multiplier is applied.'
              : 'Proposed new loads are entered at full nameplate (or MCA) rating with no further multiplier.'}
          </p>
          {/* 120.87 (2026) Condition (2): TrueLoad's reading of the new-load
              phrase, with the full-rating result printed beside it. Same text
              as the HTML report — lib/newLoadFactorDisclosure.ts. */}
          {data.new_load_factor_disclosure && (
            <p data-testid="new-load-factor-disclosure" className="mb-4 text-gray-700">
              <strong>New load under NEC {cite} ({data.code_edition}):</strong>{' '}
              {data.new_load_factor_disclosure}
            </p>
          )}
          {/* The position, not a claim that the Code settles it. The section
              applies one stated factor to the measured demand and then adds the
              new load with no multiplier stated for it; it says nothing about a
              continuous load factor on that new load, and the trade is divided.
              Saying "the Code does not prescribe" invited a reader to hear the
              silence as an answer. The paragraph now names whose reading it is,
              says the AHJ may require otherwise, and draws the line this report
              actually stops at: conductor and overcurrent sizing, which is where
              the continuous load factor genuinely lives, is not performed here.
              See context/nec-methodology.md, "Open methodology questions". */}
          <p className="mb-4 text-gray-700">
            The section states that one factor and then adds the new load without stating a multiplier for it. Whether a continuous load factor is owed on the new load is left open, and qualified opinion is divided; this evaluation applies none. That is TrueLoad's reading of an open provision, not a determination the Code makes, and the authority having jurisdiction may require otherwise. This evaluation compares calculated demand against the rating of the service only. The 125% factor for continuous loads elsewhere in the Code governs conductor and overcurrent device sizing, which is not performed here.{' '}
            {data.measured_conductor_refs
              ? `Conductor and overcurrent device sizing for individual circuits remains subject to NEC ${data.measured_conductor_refs} as applicable.`
              : 'Conductor and overcurrent device sizing for individual circuits remains subject to the applicable branch-circuit, feeder, and service provisions of the Code.'}{' '}
            Identifying which loads are continuous, and sizing for them, remains the responsibility of the licensed preparer.
          </p>
          {data.equipment_list.some((eq: any) => eq.source === 'MCA') && (
            <p className="mb-4 text-gray-700">
              <strong>MCA-Based Ratings:</strong> One or more equipment items in this evaluation use Minimum Circuit Ampacity (MCA) ratings in place of nameplate kW values. MCA is derived from kVA = MCA &times; voltage &divide; 1000. MCA is a code-minimum conductor sizing value and may overstate actual operating load; nameplate data should be used wherever available. Every rating is used exactly as entered: no conversion is performed between MCA and nameplate in either direction, so an MCA-rated row is carried at its stated MCA value.
            </p>
          )}
          {data.demand_adjusted_for_solar && data.gross_peak_kw && (
            <p className="mb-4 text-gray-700">
              <strong>Note:</strong> On-site solar generation was detected in the AMI data. Peak demand has been calculated on a gross basis (consumption plus export at each interval) in accordance with NEC {cite} Condition (1). Gross peak demand used: {parseFloat(data.gross_peak_kw).toFixed(2)} kW ({(parseFloat(data.gross_peak_kw) / 0.240).toFixed(1)} A at 240V).
            </p>
          )}
          {data.ami_generation_column_detected && !data.demand_adjusted_for_solar && (
            <p className="mb-4 text-gray-700">
              <strong>Note:</strong> On-site solar generation was detected but AMI data reflects net demand only. Gross demand could not be recovered from the available data. The calculation proceeds on measured net demand per NEC {cite} Condition (1). The licensed preparer and AHJ should evaluate whether net demand adequately represents whole-building load for this installation.
            </p>
          )}
          <p className="font-bold">
            Methodology Version: {data.methodology_version}
          </p>
          {/* A provisional edition pack states its own provenance, the way the
              traditional worksheet already does through its calculation notes —
              the reader is told which text the section numbers were read from. */}
          {!isTraditional && data.edition_note && (
            <p className="mt-2 text-xs text-gray-600">{data.edition_note}</p>
          )}
        </section>

        <div className="bg-gray-50 border border-gray-300 p-4 mb-8 text-sm">
          <p className="font-bold mb-2">IMPORTANT DISCLAIMER:</p>
          <p className="mb-3">
            This evaluation addresses service capacity only under NEC{' '}
            {isTraditional ? data.trad_citation : cite} and
            does not waive any other Code requirements including conductor
            sizing, overcurrent protection, branch circuits, grounding and
            bonding, or other applicable standards. All installations must
            comply with all applicable provisions of the National Electrical
            Code and local amendments. Final approval remains subject to the
            Authority Having Jurisdiction (AHJ).
          </p>
          <p>
            This report is based on the information provided and AMI data
            available at the time of analysis. Future changes in occupancy,
            usage patterns, or additional loads may affect service adequacy.
          </p>
        </div>

        <div className="border-t-2 border-black pt-6 mt-12">
          <div className="grid grid-cols-[200px,1fr] gap-y-4 gap-x-4">
            <div className="font-bold">Prepared By:</div>
            <div>{data.prepared_by}</div>

            <div className="font-bold">License Number:</div>
            <div>{data.preparer_license || 'N/A'}</div>

            <div className="font-bold">Date:</div>
            <div>{data.report_date}</div>

            <div className="font-bold">Signature:</div>
            <div className="border-b border-black h-12"></div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
